import { parseISO, eachDayOfInterval, startOfMonth, endOfMonth, format } from "date-fns";
import { useState, useEffect } from "react";
import {
  VictoryChart,
  VictoryAxis,
  VictoryArea,
  VictoryTooltip,
  VictoryVoronoiContainer,
} from "victory";

export default function AvailabilityGraph() {
  const [dailyAvailability, setDailyAvailability] = useState([]);
  const [staffData, setStaffData] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState(new Date());
  const [selectedDepartment, setSelectedDepartment] = useState("All Departments");
  const [shiftsData, setShiftsData] = useState([]);

  
  useEffect(() => {
    const fetchData = async () => {
      const staffResponse = await fetch("https://api.insightos.co.uk/api/staff", {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: 'include',

      });

      const responseShifts = await fetch("https://api.insightos.co.uk/api/monthshifts", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: 'include',
        body: JSON.stringify({ selectedMonth }),
      });

      const shifts = await responseShifts.json();
      setShiftsData(shifts.data);

      const staff = await staffResponse.json();
      const staffMembers = staff.data;
      setStaffData(staffMembers);

      const uniqueDepartments = ["All Departments", ...new Set(staffMembers.map((s) => s.Department))];
      setDepartments(uniqueDepartments);

      const availability = calculateDailyAvailability(shifts.data, staffMembers, selectedMonth, selectedDepartment);
      setDailyAvailability(availability);
    };

    fetchData();
  }, [selectedMonth, selectedDepartment]);

  const calculateDailyAvailability = (shiftsData, staffData, month, department) => {
    const startOfCurrentMonth = startOfMonth(month);
    const endOfCurrentMonth = endOfMonth(startOfCurrentMonth);
    const daysInMonth = eachDayOfInterval({ start: startOfCurrentMonth, end: endOfCurrentMonth });

    // Filter staff based on the selected department
    const filteredStaff = department === "All Departments" 
      ? staffData 
      : staffData.filter((s) => s.Department === department);

    const totalStaff = filteredStaff.length;

    return daysInMonth.map((day) => {
      const dayString = format(day, "yyyy-MM-dd");

      // Get shifts for the current day
      const staffOnShift = shiftsData.filter((shift) => {
        const shiftDate = format(parseISO(shift.ShiftDate), "yyyy-MM-dd");
        return shiftDate === dayString;
      });

      // Count how many of the staff on shift are on holiday or sick
      const unavailableStaff = staffOnShift.filter((shift) => shift.Holiday === 1 || shift.Sick === 1).length;

      return {
        date: format(day, "dd MMM"),
        availableStaff: staffOnShift.length - unavailableStaff,
        totalStaff,
      };
    });
  };

  const totalStaff = dailyAvailability.length > 0 ? dailyAvailability[0].totalStaff : 0;

  return (
    <div className="w-full min-h-screen rounded-3xl border border-white/35 bg-gradient-to-br from-indigo-100/75 via-sky-100/70 to-emerald-100/65 p-3 shadow-[0_16px_40px_rgba(15,23,42,0.18)] backdrop-blur-xl sm:min-h-0 sm:p-4 lg:p-6">
      {/* Header Section */}
      <div className="mb-6 rounded-2xl border border-white/30 bg-gradient-to-r from-indigo-900/90 via-sky-700/85 to-emerald-500/80 p-4 text-center shadow-[0_14px_35px_rgba(15,23,42,0.2)] sm:p-5">
        <h1 className="mb-2 flex flex-wrap items-center justify-center text-lg font-bold text-white sm:text-2xl lg:text-3xl">
          <span className="text-2xl sm:text-3xl mr-2">📊</span>
          <span className="leading-tight">Staff Availability</span>
        </h1>
        <p className="text-center text-xs text-sky-100/90 sm:text-sm">
          Track daily staff availability across departments
        </p>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row justify-center gap-3 sm:gap-6 mb-6">
        <div className="flex flex-col">
          <label className="text-xs font-semibold text-gray-700 mb-1 sm:hidden">Month</label>
          <select
            onChange={(e) => setSelectedMonth(new Date(new Date().getFullYear(), parseInt(e.target.value), 1))}
            value={selectedMonth.getMonth()}
            className="w-full rounded-2xl border border-white/45 bg-white/80 px-3 py-2.5 text-sm font-medium text-slate-800 shadow-[0_10px_26px_rgba(15,23,42,0.08)] backdrop-blur-xl transition-all duration-200 hover:border-sky-300 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 sm:w-auto sm:text-base"
          >
            {Array.from({ length: 12 }, (_, index) => (
              <option key={index} value={index}>
                {format(new Date(2025, index), "MMMM")}
              </option>
            ))}
          </select>
        </div>

        <div className="flex flex-col">
          <label className="text-xs font-semibold text-gray-700 mb-1 sm:hidden">Department</label>
          <select
            onChange={(e) => setSelectedDepartment(e.target.value)}
            value={selectedDepartment}
            className="w-full rounded-2xl border border-white/45 bg-white/80 px-3 py-2.5 text-sm font-medium text-slate-800 shadow-[0_10px_26px_rgba(15,23,42,0.08)] backdrop-blur-xl transition-all duration-200 hover:border-sky-300 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 sm:w-auto sm:text-base"
          >
            {departments.map((department, index) => (
              <option key={index} value={department}>
                {department}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Summary Stats */}
      {dailyAvailability.length > 0 && (
        <div className="mb-6 grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          <div className="rounded-2xl border border-white/35 bg-white/75 p-3 shadow-[0_10px_26px_rgba(15,23,42,0.1)] backdrop-blur-xl sm:p-4">
            <div className="text-xs sm:text-sm font-medium text-blue-700">Total Staff</div>
            <div className="text-lg sm:text-xl font-bold text-blue-800">{totalStaff}</div>
          </div>
          <div className="rounded-2xl border border-white/35 bg-white/75 p-3 shadow-[0_10px_26px_rgba(15,23,42,0.1)] backdrop-blur-xl sm:p-4">
            <div className="text-xs sm:text-sm font-medium text-green-700">Avg Available</div>
            <div className="text-lg sm:text-xl font-bold text-green-800">
              {Math.round(dailyAvailability.reduce((sum, day) => sum + day.availableStaff, 0) / dailyAvailability.length)}
            </div>
          </div>
          <div className="rounded-2xl border border-white/35 bg-white/75 p-3 shadow-[0_10px_26px_rgba(15,23,42,0.1)] backdrop-blur-xl sm:p-4">
            <div className="text-xs sm:text-sm font-medium text-purple-700">Max Available</div>
            <div className="text-lg sm:text-xl font-bold text-purple-800">
              {Math.max(...dailyAvailability.map(day => day.availableStaff))}
            </div>
          </div>
          <div className="rounded-2xl border border-white/35 bg-white/75 p-3 shadow-[0_10px_26px_rgba(15,23,42,0.1)] backdrop-blur-xl sm:p-4">
            <div className="text-xs sm:text-sm font-medium text-orange-700">Min Available</div>
            <div className="text-lg sm:text-xl font-bold text-orange-800">
              {Math.min(...dailyAvailability.map(day => day.availableStaff))}
            </div>
          </div>
        </div>
      )}

      {/* Graph */}
      <div className="overflow-hidden rounded-2xl border border-white/35 bg-white/75 p-2 shadow-[0_14px_35px_rgba(15,23,42,0.14)] backdrop-blur-xl sm:p-4">
        <div className="w-full overflow-x-auto">
          <div className="min-w-[600px] h-[300px] sm:h-[400px] lg:h-[450px]">
            <VictoryChart
              domainPadding={20}
              domain={{ y: [0, totalStaff] }}
              containerComponent={<VictoryVoronoiContainer />}
              responsive={true}
              width={600}
              height={window.innerWidth < 640 ? 300 : window.innerWidth < 1024 ? 400 : 450}
              padding={{ left: 60, top: 20, right: 40, bottom: 80 }}
            >
              {/* X-Axis */}
              <VictoryAxis
                tickValues={dailyAvailability.map((d) => d.date)}
                style={{
                  tickLabels: {
                    fill: "#1e40af", // blue-800
                    fontSize: window.innerWidth < 640 ? 8 : 10,
                    fontWeight: "bold",
                    angle: -45,
                    textAnchor: "end",
                  },
                  axis: { stroke: "#1e40af", strokeWidth: 2 }, // blue-800
                  grid: { stroke: "#e5e7eb", strokeDasharray: "2,2" }, // gray-200
                }}
              />

              {/* Y-Axis */}
              <VictoryAxis
                dependentAxis
                tickFormat={(t) => Math.floor(t)}
                style={{
                  tickLabels: { 
                    fill: "#1e40af", // blue-800
                    fontSize: window.innerWidth < 640 ? 10 : 12,
                    fontWeight: "bold"
                  },
                  axis: { stroke: "#1e40af", strokeWidth: 2 }, // blue-800
                  grid: { stroke: "#e5e7eb", strokeDasharray: "2,2" }, // gray-200
                }}
              />

              {/* Area Graph */}
              <VictoryArea
                data={dailyAvailability}
                x="date"
                y="availableStaff"
                style={{
                  data: {
                    fill: "rgba(133, 18, 234, 0.2)", // purple-800 with transparency
                    stroke: "#7900ddff", // purple-800
                    strokeWidth: 3,
                  },
                }}
                labels={({ datum }) => `Available: ${datum.availableStaff}/${totalStaff}`}
                labelComponent={
                  <VictoryTooltip 
                    style={{ 
                      fill: "#2809f5ff",
                      fontSize: 12,
                      fontWeight: "bold"
                    }}
                    flyoutStyle={{
                      stroke: "#7c2d12", // purple-800
                      strokeWidth: 2,
                      fill: "#f3f4f6", // gray-100
                    }}
                  />
                }
              />
            </VictoryChart>
          </div>
        </div>
      </div>
    </div>
  );
}
